import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const filters = ['All', 'App UI', 'Business', 'Web'];

const projects = [
  {
    id: 1,
    img: '/img/portfolio-1.jpg',
    category: 'App UI',
    title: 'Rhythmiq',
    tag: 'Music App Concept',
    accent: 'var(--aqua)',
    desc: 'A colorful music experience with station controls, polished sections, and smooth interface details for discovering audio content.',
    stack: ['React', 'UI Design', 'Responsive'],
  },
  {
    id: 2,
    img: '/img/portfolio-2.jpg',
    category: 'App UI',
    title: 'Safebox',
    tag: 'Secure Storage UI',
    accent: 'var(--rose)',
    desc: 'A premium dashboard-style concept for secure vaults, account controls, quick actions, and trust-focused visual hierarchy.',
    stack: ['Dashboard', 'Frontend', 'UX'],
  },
  {
    id: 3,
    img: '/img/portfolio-3.jpg',
    category: 'Business',
    title: 'ProspectIQ',
    tag: 'CRM Landing Page',
    accent: 'var(--sun)',
    desc: 'A sharp sales and prospecting interface concept built around lead insights, conversion blocks, and clean product storytelling.',
    stack: ['Landing Page', 'SaaS UI', 'Marketing'],
  },
  {
    id: 4,
    img: '/img/portfolio-4.jpg',
    category: 'Web',
    title: 'Restaurant Website',
    tag: 'Food Brand Website',
    accent: 'var(--lime)',
    desc: 'A vivid restaurant website direction with menu highlights, booking flow, gallery sections, and mobile-first browsing.',
    stack: ['HTML', 'CSS', 'JavaScript'],
  },
  {
    id: 5,
    img: '/img/portfolio-5.jpg',
    category: 'Web',
    title: 'Portfolio Website',
    tag: 'Personal Brand',
    accent: 'var(--violet)',
    desc: 'A personal site layout focused on strong first impression, smooth scrolling, service clarity, and clean contact paths.',
    stack: ['Portfolio', 'Animation', 'SEO'],
  },
  {
    id: 6,
    img: '/img/portfolio-6.jpg',
    category: 'Business',
    title: 'Retail Website',
    tag: 'Business Presence',
    accent: 'var(--aqua)',
    desc: 'A responsive business website structure with product sections, trust blocks, service details, and conversion-focused CTAs.',
    stack: ['Business', 'CMS', 'Responsive'],
  },
];

export default function Portfolio() {
  const [active, setActive] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });

  const filtered = active === 'All' ? projects : projects.filter(project => project.category === active);

  return (
    <section id="portfolio" ref={ref} className="section-pad">
      <div className="ghost-text">PROJECTS</div>

      <div className="section-shell">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 24, alignItems: 'end', marginBottom: 38 }}
          className="portfolio-head"
        >
          <div>
            <div className="eyebrow">Selected work</div>
            <h2 className="section-title">Featured projects with stronger visual energy.</h2>
            <p className="section-copy">
              Rhythmiq, Safebox, ProspectIQ, and a restaurant website are now placed as premium case-study cards.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            {filters.map(filter => (
              <button
                key={filter}
                onClick={() => setActive(filter)}
                className={active === filter ? 'btn-primary' : 'btn-secondary'}
                style={{ minHeight: 40, padding: '9px 14px', fontSize: 10 }}
              >
                {filter}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="portfolio-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 18 }}>
          <AnimatePresence mode="popLayout">
            {filtered.map((project, index) => (
              <motion.article
                layout
                key={project.id}
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.96 }}
                transition={{ duration: 0.42, delay: index * 0.04 }}
                whileHover={{ y: -8 }}
                onClick={() => setLightbox(project)}
                className="premium-card shine"
                style={{ overflow: 'hidden', cursor: 'pointer', minHeight: 520 }}
              >
                <div style={{ position: 'relative', height: 255, overflow: 'hidden' }}>
                  <img
                    src={project.img}
                    alt={`${project.title} project by Prakash C, web and app developer in Chennai`}
                    loading={index < 3 ? 'eager' : 'lazy'}
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, rgba(7,7,8,0.86), transparent 52%), linear-gradient(135deg, ${project.accent}, transparent 42%)`, mixBlendMode: 'normal', opacity: 0.82 }} />
                  <span className="chip" style={{ position: 'absolute', top: 14, left: 14, color: project.accent }}>{project.category}</span>
                </div>

                <div style={{ padding: 24 }}>
                  <div style={{ color: project.accent, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 9 }}>
                    {project.tag}
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 25, lineHeight: 1.1, marginBottom: 12 }}>{project.title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.72, marginBottom: 18 }}>{project.desc}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 22 }}>
                    {project.stack.map(item => <span key={item} className="chip">{item}</span>)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--white)' }}>
                      View details
                    </span>
                    <span style={{ color: project.accent, fontFamily: 'var(--font-display)', fontWeight: 800 }}>+</span>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9000,
              padding: 22,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(0,0,0,0.78)',
              backdropFilter: 'blur(14px)',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              onClick={event => event.stopPropagation()}
              className="premium-card"
              style={{ width: 'min(960px, 100%)', overflow: 'hidden' }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }} className="project-modal-grid">
                <img src={lightbox.img} alt={`${lightbox.title} portfolio case study by Prakash C`} style={{ width: '100%', height: '100%', minHeight: 420, objectFit: 'cover' }} />
                <div style={{ padding: 34 }}>
                  <div className="chip" style={{ color: lightbox.accent, marginBottom: 18 }}>{lightbox.category}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.2rem)', lineHeight: 0.95, marginBottom: 18 }}>
                    {lightbox.title}
                  </h3>
                  <p style={{ color: 'var(--soft)', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{lightbox.desc}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
                    {lightbox.stack.map(item => <span key={item} className="chip">{item}</span>)}
                  </div>
                  <button onClick={() => setLightbox(null)} className="btn-secondary" style={{ minWidth: 150 }}>
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 1040px) {
          .portfolio-grid { grid-template-columns: 1fr 1fr !important; }
          .portfolio-head { grid-template-columns: 1fr !important; }
          .portfolio-head > div:last-child { justify-content: flex-start !important; }
        }
        @media (max-width: 680px) {
          .portfolio-grid { grid-template-columns: 1fr !important; }
          .project-modal-grid { grid-template-columns: 1fr !important; }
          .project-modal-grid img { min-height: 260px !important; }
        }
      `}</style>
    </section>
  );
}
